import { ForceTheme } from 'src/common/components/force-theme';
import type { UserOccupation } from 'src/common/constants/user';
import { ThemeColorMode } from 'src/common/enums';
import { useGetUserQuery } from 'src/common/slices';
import { useActiveCampaign } from 'src/features/campaigns/hooks/use-active-campaign';
import { SubscribeNowLabel, useTrackSubscribeNow } from 'src/features/campaigns/hooks/use-track-subscribe-now';
import { useYearlyPricing } from 'src/features/campaigns/hooks/use-yearly-pricing';
import { Countdown } from '../../components/countdown';
import { useSubscribeNowCtaLink } from '../../hooks/use-subscribe-now-cta-link';
import { CopyType, getModalCopy } from '../modal/modal.constants';
import { SubscribeButton } from '../subscribe-button';
import { StyledBanner, StyledButtons, StyledCountdownContainer, StyledOpeningDiscountLetter, StyledSubTitle, StyledSubTitleAlt, StyledTitle } from './banner.styles';
import type { CybersecurityAwarenessProps } from './banner.types';
import { DiscountMessage } from './components/discount-message';
export const CybersecurityAwarenessBanner = ({
  title,
  subtitle,
  subtitleAlt,
  isLarge,
  ctaLabel,
  ctaName,
  onCtaClick
}: CybersecurityAwarenessProps) => {
  const userQuery = useGetUserQuery();
  const {
    campaign
  } = useActiveCampaign();
  const {
    yearlyPrice,
    discountedYearlyPrice,
    symbol
  } = useYearlyPricing();
  const ctaLink = useSubscribeNowCtaLink(campaign);
  const trackSubscribeNow = useTrackSubscribeNow();
  const user = userQuery.data?.data?.user;
  const isPremium = Boolean(user?.isPremium);
  if (!campaign) return null;
  const onClick = () => {
    trackSubscribeNow(ctaName);
    onCtaClick?.();
  };
  return <ForceTheme themeMode={ThemeColorMode.DARK} data-sentry-element="ForceTheme" data-sentry-component="CybersecurityAwarenessBanner" data-sentry-source-file="banner.tsx">
      <StyledBanner data-sentry-element="StyledBanner" data-sentry-source-file="banner.tsx">
        <StyledOpeningDiscountLetter $large={isLarge} alt="" aria-hidden="true" data-sentry-element="StyledOpeningDiscountLetter" data-sentry-source-file="banner.tsx" />
        <StyledTitle data-testid="campaign-title" data-sentry-element="StyledTitle" data-sentry-source-file="banner.tsx">{title}</StyledTitle>
        {subtitle && <StyledSubTitle data-testid="campaign-promotion-text">{subtitle}</StyledSubTitle>}
        {subtitleAlt && <StyledSubTitleAlt data-testid="campaign-promotion-text-alt">{subtitleAlt}</StyledSubTitleAlt>}
        <StyledCountdownContainer data-sentry-element="StyledCountdownContainer" data-sentry-source-file="banner.tsx">
          <Countdown vertical dark endTime={campaign.endTime} data-sentry-element="Countdown" data-sentry-source-file="banner.tsx" />
        </StyledCountdownContainer>
        <DiscountMessage isPremium={isPremium} yearlyPrice={yearlyPrice} discountedYearlyPrice={discountedYearlyPrice} symbol={symbol} data-sentry-element="DiscountMessage" data-sentry-source-file="banner.tsx" />
        <StyledButtons data-sentry-element="StyledButtons" data-sentry-source-file="banner.tsx">
          <SubscribeButton to={ctaLink} aria-label={ctaLabel} onClick={onClick} data-sentry-element="SubscribeButton" data-sentry-source-file="banner.tsx">
            {ctaLabel}
          </SubscribeButton>
        </StyledButtons>
      </StyledBanner>
    </ForceTheme>;
};
export const withModalStyling = (BaseComponent: typeof CybersecurityAwarenessBanner) => {
  return function ModalCampaignBanner(props: {
    onClick: () => void;
    occupation?: string;
    isPremium?: boolean;
  }) {
    const {
      isPremium,
      occupation,
      onClick
    } = props;
    const copy = getModalCopy(isPremium ? CopyType.PREMIUM : CopyType.FREE, occupation as UserOccupation);
    return <BaseComponent title={copy.title} subtitle={copy.subtitle} subtitleAlt={undefined} isLarge={false} ctaLabel={isPremium ? 'Switch to annual' : 'Subscribe now'} ctaName={SubscribeNowLabel.FlashSaleModal} onCtaClick={onClick} />;
  };
};